import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  BarChart3,
  TrendingUp,
  CreditCard,
  Package,
  Coins,
  Calendar,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { paymentAdminAPI } from '../../services/api';
import { addDaysToBeijingDateKey, formatBeijingDateKey } from '../../utils/time';

interface DailyStat {
  date: string;
  amount: number;
  orders: number;
}

interface PayTypeStat {
  payType: string;
  amount: number;
  credits: number;
  orders: number;
}

interface StatsData {
  total: {
    amount: number;
    credits: number;
    orders: number;
  };
  byPayType: PayTypeStat[];
  daily: DailyStat[];
}

const payTypeMap: Record<string, string> = {
  alipay: '支付宝',
  wxpay: '微信支付',
  qqpay: 'QQ支付',
};

const payTypeColors: Record<string, string> = {
  alipay: 'bg-blue-500',
  wxpay: 'bg-green-500',
  qqpay: 'bg-purple-500',
};

function StatCard({
  title,
  value,
  suffix,
  icon: Icon,
  color,
}: {
  title: string;
  value: number;
  suffix?: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
}) {
  return (
    <div className="bg-white rounded-2xl p-5 shadow-lg border border-light-border">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-light-muted text-sm">{title}</p>
          <p className="text-2xl font-bold text-light-text mt-2 sm:text-3xl">
            {value.toLocaleString()}
            {suffix ? <span className="text-lg ml-1">{suffix}</span> : null}
          </p>
        </div>
        <div className={`w-12 h-12 rounded-xl ${color} flex items-center justify-center`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </div>
  );
}

function SimpleLineChart({ data }: { data: DailyStat[] }) {
  if (data.length === 0) return null;

  const [activePoint, setActivePoint] = useState<{
    date: string;
    amount: number;
    x: number;
    y: number;
  } | null>(null);

  const chartWidth = 760;
  const chartHeight = 300;
  const padding = { top: 20, right: 32, bottom: 44, left: 64 };
  const innerWidth = chartWidth - padding.left - padding.right;
  const innerHeight = chartHeight - padding.top - padding.bottom;
  const maxAmount = Math.max(...data.map((d) => d.amount), 1);
  const tickCount = 4;

  const amountTicks = Array.from({ length: tickCount + 1 }, (_, index) => {
    const value = (maxAmount / tickCount) * (tickCount - index);
    const y = padding.top + (innerHeight / tickCount) * index;
    return { value, y };
  });

  const points = data.map((item, index) => {
    const x = padding.left + (data.length === 1 ? innerWidth / 2 : (index / (data.length - 1)) * innerWidth);
    const amountY = padding.top + innerHeight - (item.amount / maxAmount) * innerHeight;
    return {
      ...item,
      shortDate: item.date.slice(5),
      x,
      amountY,
    };
  });

  const amountPath = points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.amountY}`).join(' ');
  const xLabelStep = Math.max(1, Math.ceil(data.length / 7));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 text-xs text-light-muted">
        <span className="inline-flex items-center gap-2 rounded-full bg-amber-50 px-3 py-1 text-amber-700">
          <span className="h-2.5 w-2.5 rounded-full bg-amber-500" />
          支付金额
        </span>
        <span>移动到节点附近可查看日期和金额</span>
      </div>

      <div className="relative overflow-x-auto">
        {activePoint ? (
          <div
            className="pointer-events-none absolute z-10 rounded-xl border border-amber-200 bg-white/95 px-3 py-2 shadow-lg backdrop-blur"
            style={{
              left: `clamp(12px, calc(${((activePoint.x / chartWidth) * 100).toFixed(2)}% - 56px), calc(100% - 132px))`,
              top: Math.max(8, activePoint.y - 72),
            }}
          >
            <div className="text-xs text-light-muted">{activePoint.date}</div>
            <div className="mt-1 text-sm font-semibold text-amber-700">
              ¥{activePoint.amount.toFixed(2)}
            </div>
          </div>
        ) : null}
        <svg
          viewBox={`0 0 ${chartWidth} ${chartHeight}`}
          className="min-w-[680px] w-full"
          role="img"
          aria-label="订单金额趋势折线图"
          onMouseLeave={() => setActivePoint(null)}
        >
          {amountTicks.map((tick, index) => (
            <g key={`grid-${index}`}>
              <line
                x1={padding.left}
                x2={chartWidth - padding.right}
                y1={tick.y}
                y2={tick.y}
                stroke="#e5e7eb"
                strokeDasharray="4 4"
              />
              <text x={padding.left - 10} y={tick.y + 4} textAnchor="end" fontSize="11" fill="#6b7280">
                ¥{tick.value.toFixed(0)}
              </text>
            </g>
          ))}

          <line
            x1={padding.left}
            x2={chartWidth - padding.right}
            y1={padding.top + innerHeight}
            y2={padding.top + innerHeight}
            stroke="#d1d5db"
          />

          <path d={amountPath} fill="none" stroke="#d97706" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />

          {points.map((point, index) => (
            <g key={point.date}>
              <circle
                cx={point.x}
                cy={point.amountY}
                r="14"
                fill="transparent"
                className="cursor-pointer"
                onMouseEnter={() =>
                  setActivePoint({
                    date: point.date,
                    amount: point.amount,
                    x: point.x,
                    y: point.amountY,
                  })
                }
              />
              <circle
                cx={point.x}
                cy={point.amountY}
                r={activePoint?.date === point.date ? '7' : '5'}
                fill="#d97706"
                stroke={activePoint?.date === point.date ? '#fef3c7' : 'none'}
                strokeWidth={activePoint?.date === point.date ? '4' : '0'}
              />
              {(index % xLabelStep === 0 || index === points.length - 1) && (
                <text
                  x={point.x}
                  y={chartHeight - 14}
                  textAnchor="middle"
                  fontSize="11"
                  fill="#6b7280"
                >
                  {point.shortDate}
                </text>
              )}
            </g>
          ))}
        </svg>
      </div>

      <div className="grid gap-2 sm:grid-cols-3">
        {data.slice(-3).map((item) => (
          <div key={item.date} className="rounded-xl border border-light-border bg-light-hover/40 px-3 py-2">
            <p className="text-xs text-light-muted">{item.date}</p>
            <div className="mt-1 text-xs font-medium text-amber-700">¥{item.amount.toFixed(2)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function OrderStatsTab() {
  const [stats, setStats] = useState<StatsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [startDate, setStartDate] = useState(() => {
    return addDaysToBeijingDateKey(formatBeijingDateKey(), -30);
  });
  const [endDate, setEndDate] = useState(() => formatBeijingDateKey());

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await paymentAdminAPI.getStats({
        startDate,
        endDate,
      });
      setStats(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : '获取统计失败');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const chartData = useMemo(() => {
    if (!stats) return [];
    return [...stats.daily].sort((a, b) => a.date.localeCompare(b.date));
  }, [stats]);

  const handleQuickSelect = (days: number) => {
    const end = formatBeijingDateKey();
    setEndDate(end);
    setStartDate(addDaysToBeijingDateKey(end, -days));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-accent-blue" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-20">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <p className="text-red-500 mb-4">{error}</p>
        <button
          onClick={fetchStats}
          className="px-4 py-2 bg-light-primary text-white rounded-xl hover:bg-light-primary/90 transition-colors"
        >
          重试
        </button>
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div className="space-y-6">
      {/* 日期筛选 */}
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-light-border">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-light-muted" />
            <h3 className="font-semibold text-light-text">订单统计</h3>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
            <div className="flex flex-wrap gap-1">
              {[7, 30, 90].map((days) => (
                <button
                  key={days}
                  onClick={() => handleQuickSelect(days)}
                  className="px-3 py-1.5 rounded-lg text-sm bg-light-hover text-light-text hover:bg-light-border transition-colors"
                >
                  近{days}天
                </button>
              ))}
            </div>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 rounded-xl border border-light-border text-sm focus:outline-none focus:ring-2 focus:ring-light-primary/20 sm:w-auto"
            />
            <span className="text-light-muted">至</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 rounded-xl border border-light-border text-sm focus:outline-none focus:ring-2 focus:ring-light-primary/20 sm:w-auto"
            />
            <button
              onClick={fetchStats}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-light-primary text-white rounded-xl hover:bg-light-primary/90 transition-colors"
            >
              <TrendingUp className="w-4 h-4" />
              查询
            </button>
          </div>
        </div>
      </div>

      {/* 汇总卡片 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
        <StatCard
          title="总订单数"
          value={stats.total.orders}
          icon={Package}
          color="bg-blue-500"
        />
        <StatCard
          title="总金额"
          value={stats.total.amount}
          suffix="元"
          icon={CreditCard}
          color="bg-green-500"
        />
        <StatCard
          title="总积分"
          value={stats.total.credits}
          icon={Coins}
          color="bg-purple-500"
        />
      </div>

      {/* 支付方式统计 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl p-5 shadow-lg border border-light-border">
          <div className="flex items-center gap-2 mb-6">
            <CreditCard className="w-5 h-5 text-light-muted" />
            <h3 className="font-semibold text-light-text">支付方式统计</h3>
          </div>
          {stats.byPayType.length === 0 ? (
            <p className="text-sm text-light-muted text-center py-8">暂无数据</p>
          ) : (
            <div className="space-y-4">
              {stats.byPayType.map((item) => (
                <div key={item.payType} className="flex items-center gap-4">
                  <div className={`w-3 h-3 rounded-full ${payTypeColors[item.payType] || 'bg-gray-400'}`} />
                  <span className="text-sm text-light-text w-20">
                    {payTypeMap[item.payType] || item.payType}
                  </span>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-light-muted">
                        {item.orders} 单
                      </span>
                      <span className="text-sm font-medium text-light-text">
                        ¥{item.amount.toFixed(2)}
                      </span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2">
                      <div
                        className={`${payTypeColors[item.payType] || 'bg-gray-400'} h-2 rounded-full transition-all`}
                        style={{
                          width: `${stats.total.amount > 0 ? (item.amount / stats.total.amount) * 100 : 0}%`,
                        }}
                      />
                    </div>
                  </div>
                  <span className="text-sm text-accent-blue w-16 text-right">
                    {item.credits} 积分
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 每日趋势 */}
        <div className="bg-white rounded-2xl p-5 shadow-lg border border-light-border">
          <div className="flex items-center gap-2 mb-6">
            <Calendar className="w-5 h-5 text-light-muted" />
            <h3 className="font-semibold text-light-text">每日趋势</h3>
          </div>
          {stats.daily.length === 0 ? (
            <p className="text-sm text-light-muted text-center py-8">暂无数据</p>
          ) : (
            <SimpleLineChart data={chartData} />
          )}
        </div>
      </div>

      {/* 数据表格 */}
      <div className="bg-white rounded-2xl shadow-lg border border-light-border overflow-hidden">
        <div className="p-6 border-b border-light-border">
          <h3 className="font-semibold text-light-text">每日明细</h3>
        </div>
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-light-border bg-light-hover">
                <th className="text-left text-sm font-medium text-light-muted py-3 px-4">日期</th>
                <th className="text-right text-sm font-medium text-light-muted py-3 px-4">订单数</th>
                <th className="text-right text-sm font-medium text-light-muted py-3 px-4">金额</th>
                <th className="text-right text-sm font-medium text-light-muted py-3 px-4">占比</th>
              </tr>
            </thead>
            <tbody>
              {stats.daily.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center py-8 text-light-muted">
                    暂无数据
                  </td>
                </tr>
              ) : (
                stats.daily.map((item) => (
                  <tr key={item.date} className="border-b border-light-border last:border-0 hover:bg-light-hover/50">
                    <td className="py-3 px-4 text-sm text-light-text">{item.date}</td>
                    <td className="py-3 px-4 text-sm text-light-text text-right">{item.orders}</td>
                    <td className="py-3 px-4 text-sm font-medium text-light-text text-right">
                      ¥{item.amount.toFixed(2)}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <DailyRatio amount={item.amount} totalAmount={stats.total.amount} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="divide-y divide-light-border lg:hidden">
          {stats.daily.length === 0 ? (
            <div className="py-8 text-center text-light-muted">暂无数据</div>
          ) : (
            stats.daily.map((item) => (
              <div key={item.date} className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-medium text-light-text">{item.date}</div>
                    <div className="mt-1 text-xs text-light-muted">{item.orders} 单</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-light-muted">金额</div>
                    <div className="text-sm font-medium text-light-text">¥{item.amount.toFixed(2)}</div>
                  </div>
                </div>
                <DailyRatio amount={item.amount} totalAmount={stats.total.amount} mobile />
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function DailyRatio({
  amount,
  totalAmount,
  mobile = false,
}: {
  amount: number;
  totalAmount: number;
  mobile?: boolean;
}) {
  const ratio = totalAmount > 0 ? (amount / totalAmount) * 100 : 0;
  return (
    <div className={`flex items-center gap-2 ${mobile ? '' : 'justify-end'}`}>
      <div className={`${mobile ? 'flex-1' : 'w-20'} bg-gray-100 rounded-full h-2`}>
        <div
          className="bg-accent-blue h-2 rounded-full transition-all"
          style={{ width: `${ratio}%` }}
        />
      </div>
      <span className={`text-xs text-light-muted ${mobile ? 'w-12 text-right' : 'w-12'}`}>
        {ratio.toFixed(1)}%
      </span>
    </div>
  );
}
